import type { ApprovalListQuery, ApprovalStateDto } from '../models/platformAction.wire'
import { adaptInboxPage } from '../service/adaptActionRequest'
import { classifyError, isCanceled } from '../service/classifyError'

import type {
  ApprovalInboxApi,
  ApprovalInboxQuery,
  ApprovalInboxStore,
} from './platformActionFlow.types'

export interface LoadApprovalsFlowDeps {
  api: ApprovalInboxApi
  store: ApprovalInboxStore
}

export function approvalsKey(workspaceId: number, query: ApprovalInboxQuery): string {
  return [workspaceId, query.state, query.page, query.pageSize].join('|')
}

/**
 * `include_closed` is always on: `closed` rows only exist in the widened candidate set, and asking
 * for history whenever the filter is off keeps "All" honest too.
 */
function buildQuery(query: ApprovalInboxQuery): ApprovalListQuery {
  const params: ApprovalListQuery = {
    include_closed: true,
    page: query.page,
    page_size: query.pageSize,
  }
  if (query.state) params.approval_state = query.state as ApprovalStateDto
  return params
}

/**
 * The approval inbox. This flow does not own approval state and never pretends to: the same
 * decision can be recorded from chat or Slack, so the page re-runs this after every decision and on
 * an interval instead of patching rows in place.
 */
export class LoadApprovalsFlow {
  constructor(private readonly deps: LoadApprovalsFlowDeps) {}

  async execute(
    workspaceId: number,
    query: ApprovalInboxQuery,
    options: { silent?: boolean } = {},
    signal?: AbortSignal,
  ): Promise<void> {
    const key = approvalsKey(workspaceId, query)
    // A background refresh keeps the rows on screen; only a new query shows the skeleton.
    if (!options.silent) this.deps.store.setLoading(key)

    try {
      const body = await this.deps.api.listApprovals(workspaceId, buildQuery(query), signal)
      if (signal?.aborted === true) return
      this.deps.store.setPage(key, adaptInboxPage(body))
    } catch (cause) {
      const error = classifyError(cause)
      if (isCanceled(error)) return
      this.deps.store.setError(key, error)
    }
  }

  /** The tab badge. A badge that cannot be read is a badge that is not shown, never an error. */
  async count(workspaceId: number, signal?: AbortSignal): Promise<void> {
    try {
      const body = await this.deps.api.approvalsCount(workspaceId, signal)
      if (signal?.aborted === true) return
      const value = body?.awaiting_decision
      this.deps.store.setAwaitingDecision(typeof value === 'number' && value > 0 ? value : 0)
    } catch {
      this.deps.store.setAwaitingDecision(0)
    }
  }
}
